import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { companyInfo, products } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    productInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/inquiries`, formData);

      if (response.status === 201) {
        toast.success('Inquiry submitted successfully! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          productInterest: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white pt-24 sm:pt-28 pb-16 sm:pb-24">
      {/* Page Header */}
      <section className="py-10 sm:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="font-rajdhani text-brand font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase mb-2 sm:mb-3">
            Get In Touch
          </p>
          <h1 className="font-rajdhani text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800">
            Contact Us
          </h1>
          <p className="font-roboto text-slate-500 mt-3 sm:mt-4 text-sm sm:text-base max-w-xl mx-auto">
            Let's discuss your architectural glass requirements
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-8 sm:gap-10">
            {/* Contact Information */}
            <div className="lg:col-span-2 space-y-6 sm:space-y-8">
              <div className="p-6 sm:p-8 bg-slate-50 border border-slate-200">
                <h2 className="font-rajdhani text-xl sm:text-2xl font-bold text-slate-800 mb-6 sm:mb-8">Get In Touch</h2>

                <div className="space-y-5 sm:space-y-6">
                  <div className="flex items-start gap-3 sm:gap-4 group">
                    <div className="p-2.5 sm:p-3 bg-brand-50 text-brand group-hover:bg-brand group-hover:text-white transition-all flex-shrink-0">
                      <Phone size={20} />
                    </div>
                    <div>
                      <div className="font-roboto text-slate-500 text-xs mb-1 uppercase tracking-wider">Phone</div>
                      <a href={`tel:${companyInfo.phone}`} data-testid="contact-phone" className="font-roboto text-slate-800 font-medium hover:text-brand transition-colors text-sm">
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4 group">
                    <div className="p-2.5 sm:p-3 bg-brand-50 text-brand group-hover:bg-brand group-hover:text-white transition-all flex-shrink-0">
                      <Mail size={20} />
                    </div>
                    <div>
                      <div className="font-roboto text-slate-500 text-xs mb-1 uppercase tracking-wider">Email</div>
                      <a href={`mailto:${companyInfo.email}`} data-testid="contact-email" className="font-roboto text-slate-800 font-medium hover:text-brand transition-colors text-sm break-all">
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4 group">
                    <div className="p-2.5 sm:p-3 bg-brand-50 text-brand group-hover:bg-brand group-hover:text-white transition-all flex-shrink-0">
                      <MapPin size={20} />
                    </div>
                    <div>
                      <div className="font-roboto text-slate-500 text-xs mb-1 uppercase tracking-wider">Address</div>
                      <p className="font-roboto text-slate-800 font-medium leading-relaxed text-sm">
                        {companyInfo.address}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-5 sm:p-6 bg-brand-50 border border-brand/10">
                <h3 className="font-rajdhani text-base sm:text-lg font-bold text-slate-800 mb-2">
                  Quick Response Guarantee
                </h3>
                <p className="font-roboto text-slate-600 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                  We typically respond to inquiries within 24 hours. For urgent requirements,
                  please call us directly.
                </p>
                <div className="flex items-center gap-2 text-brand">
                  <CheckCircle2 size={16} />
                  <span className="font-roboto text-xs sm:text-sm font-medium">24-hour response time</span>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} data-testid="contact-form" className="p-6 sm:p-8 bg-slate-50 border border-slate-200">
                <h2 className="font-rajdhani text-xl sm:text-2xl font-bold text-slate-800 mb-6 sm:mb-8">Send Us an Inquiry</h2>

                <div className="space-y-4 sm:space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                    <div>
                      <label className="block font-roboto text-slate-700 text-xs sm:text-sm font-medium mb-1.5 sm:mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="input-name"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-slate-300 text-slate-800 font-roboto text-sm placeholder-slate-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20 transition-all"
                        placeholder="Your full name"
                      />
                    </div>

                    <div>
                      <label className="block font-roboto text-slate-700 text-xs sm:text-sm font-medium mb-1.5 sm:mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="input-email"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-slate-300 text-slate-800 font-roboto text-sm placeholder-slate-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20 transition-all"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                    <div>
                      <label className="block font-roboto text-slate-700 text-xs sm:text-sm font-medium mb-1.5 sm:mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        data-testid="input-phone"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-slate-300 text-slate-800 font-roboto text-sm placeholder-slate-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20 transition-all"
                        placeholder="+91-XXXXXXXXXX"
                      />
                    </div>

                    <div>
                      <label className="block font-roboto text-slate-700 text-xs sm:text-sm font-medium mb-1.5 sm:mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        data-testid="input-company"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-slate-300 text-slate-800 font-roboto text-sm placeholder-slate-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20 transition-all"
                        placeholder="Your company name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-roboto text-slate-700 text-xs sm:text-sm font-medium mb-1.5 sm:mb-2">
                      Product Interest
                    </label>
                    <select
                      name="productInterest"
                      value={formData.productInterest}
                      onChange={handleChange}
                      data-testid="select-product-interest"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-slate-300 text-slate-800 font-roboto text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20 transition-all"
                    >
                      <option value="">Select a product</option>
                      {products.map((product) => (
                        <option key={product.id} value={product.name}>
                          {product.name}
                        </option>
                      ))}
                      <option value="Other">Other / Custom Solution</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-roboto text-slate-700 text-xs sm:text-sm font-medium mb-1.5 sm:mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                      data-testid="input-message"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-slate-300 text-slate-800 font-roboto text-sm placeholder-slate-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20 transition-all resize-none"
                      placeholder="Tell us about your project requirements..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="submit-inquiry"
                    className="w-full py-3 sm:py-4 bg-brand hover:bg-brand-dark text-white font-rajdhani font-semibold text-base sm:text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        SUBMIT INQUIRY
                      </>
                    )}
                  </button>

                  <p className="font-roboto text-slate-400 text-[10px] sm:text-xs text-center">
                    By submitting this form, you agree to our terms and conditions
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 p-4 sm:p-6 border-b border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
              <div>
                <h3 className="font-rajdhani text-lg sm:text-xl font-bold text-slate-800">Visit Our Manufacturing Facility</h3>
                <p className="font-roboto text-slate-500 text-xs sm:text-sm mt-1">Jhotwara Industrial Area, Jaipur</p>
              </div>
              <a
                href="https://maps.app.goo.gl/bYNiuqoz8GgA17RH7"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-google-maps"
                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-rajdhani font-semibold px-4 sm:px-6 py-2.5 sm:py-3 text-sm transition-colors"
              >
                <MapPin size={16} />
                Open in Maps
              </a>
            </div>
            <div className="aspect-video sm:aspect-[21/9]">
              <iframe
                src="https://maps.google.com/maps?q=26.9556505,75.7599564&hl=en&z=16&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="SynergyTuff India Location"
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
